import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { BookOpen, Download, RefreshCcw, Search } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import "./PaieCyclePages.css";

const userName = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const money = (value) => {
    const n = Number(value || 0);
    const fixed = n.toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${intFormatted},${decPart}`;
};

const amount = (value) => Number(value || 0);
const monthLabel = (periode) => periode ? `${String(periode.mois).padStart(2, "0")}/${periode.annee}` : "-";

const PRIMARY = [32, 67, 133];    // #2f4595

export default function JournalDePaiePage() {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [selectedPeriodeId, setSelectedPeriodeId] = useState("");
    const [q, setQ] = useState("");
    const [matriculeDebut, setMatriculeDebut] = useState("");
    const [matriculeFin, setMatriculeFin] = useState("");

    useEffect(() => {
        dispatch(getAllUsersRequest());
        ["periodes", "bulletins"].forEach((resource) => dispatch(payrollFetchResourceRequest(resource)));
    }, [dispatch]);

    useEffect(() => {
        if (!selectedPeriodeId && payroll.periodes?.length > 0) {
            setSelectedPeriodeId(String(payroll.periodes[0].id));
        }
    }, [payroll.periodes, selectedPeriodeId]);

    const activePeriod = useMemo(() => (payroll.periodes || []).find((p) => String(p.id) === String(selectedPeriodeId)), [payroll.periodes, selectedPeriodeId]);
    const bulletins = useMemo(() => (payroll.bulletins || []).filter((b) => !selectedPeriodeId || String(b.periodeId) === String(selectedPeriodeId)), [payroll.bulletins, selectedPeriodeId]);

    const filteredBulletins = useMemo(() => {
        return bulletins.filter((b) => {
            const user = users.find((u) => u.id === b.userId);
            const code = user?.employeeCode || b.employeeCode || "";
            const nameMatch = !q.trim() || userName(user).toLowerCase().includes(q.toLowerCase());
            const startOk = !matriculeDebut.trim() || code.toLowerCase() >= matriculeDebut.trim().toLowerCase();
            const endOk = !matriculeFin.trim() || code.toLowerCase() <= matriculeFin.trim().toLowerCase();
            return nameMatch && startOk && endOk;
        });
    }, [bulletins, users, q, matriculeDebut, matriculeFin]);

    const downloadJournal = () => {
        if (!filteredBulletins.length) {
            Swal.fire({ icon: "warning", text: "Aucun bulletin disponible." });
            return;
        }
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        doc.setFillColor(...PRIMARY);
        doc.rect(0, 0, 297, 10, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`Journal de paie - ${monthLabel(activePeriod)}`, 148, 7, { align: "center" });
        doc.setTextColor(0, 0, 0);

        const rows = filteredBulletins.map((b) => {
            const u = users.find((user) => user.id === b.userId);
            return [
                u?.employeeCode || b.employeeCode || "-",
                userName(u),
                money(b.salaireBase),
                money(b.nombreJoursTravailles),
                money(b.montantHeuresSupplementaires),
                money(b.salaireBrutImposable),
                money(b.fraisProfessionnels),
                money(b.totalCotisationsSalariales),
                money(b.ir),
                money(b.totalCotisationsPatronales),
                money(b.netAPayer),
                money(b.arrondi),
            ];
        });
        const totals = filteredBulletins.reduce((acc, b) => ({
            salaireBase: acc.salaireBase + amount(b.salaireBase),
            jours: acc.jours + amount(b.nombreJoursTravailles),
            hs: acc.hs + amount(b.montantHeuresSupplementaires),
            brut: acc.brut + amount(b.salaireBrutImposable),
            frais: acc.frais + amount(b.fraisProfessionnels),
            cotisations: acc.cotisations + amount(b.totalCotisationsSalariales),
            ir: acc.ir + amount(b.ir),
            patronal: acc.patronal + amount(b.totalCotisationsPatronales),
            net: acc.net + amount(b.netAPayer),
            arrondi: acc.arrondi + amount(b.arrondi),
        }), { salaireBase: 0, jours: 0, hs: 0, brut: 0, frais: 0, cotisations: 0, ir: 0, patronal: 0, net: 0, arrondi: 0 });
        autoTable(doc, {
            startY: 14,
            margin: { left: 8, right: 8 },
            theme: "grid",
            styles: { fontSize: 6, cellPadding: 1.2 },
            headStyles: { fillColor: PRIMARY, textColor: 255, halign: "center" },
            head: [["Matricule", "Nom & Prénom", "Salaire de base", "Jours", "Montant HS", "Brut imposable", "Frais prof.", "Cotisation sal.", "Retenue IR", "Cotisation PP", "Net à payer", "Arrondi"]],
            body: rows,
            foot: [["", "Total général", money(totals.salaireBase), money(totals.jours), money(totals.hs), money(totals.brut), money(totals.frais), money(totals.cotisations), money(totals.ir), money(totals.patronal), money(totals.net), money(totals.arrondi)]],
            footStyles: { fillColor: [240, 244, 252], textColor: 0, fontStyle: "bold" },
        });
        doc.save(`Journal_Paie_${monthLabel(activePeriod)}.pdf`);
    };

    const btnPrimary = { background: "#2f4595", color: "#fff", border: "none" };
    const btnAccent  = { background: "#dc3545", color: "#fff", border: "none" };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><BookOpen size={18} /> Journal de Paie</h2>
                    <p>Visualisation et telechargement de l'état récapitulatif détaillé du journal de paie.</p>
                </div>
                <button className="pcyc-btn" style={btnPrimary} type="button" onClick={() => dispatch(payrollFetchResourceRequest("bulletins"))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>

            <div className="pcyc-toolbar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap" }}>
                    <label style={{ minWidth: "200px" }}>
                        <span>Période</span>
                        <select value={selectedPeriodeId} onChange={(e) => setSelectedPeriodeId(e.target.value)}>
                            <option value="">Sélectionner</option>
                            {(payroll.periodes || []).map((p) => <option key={p.id} value={p.id}>{monthLabel(p)}</option>)}
                        </select>
                    </label>
                    <label style={{ width: "120px" }}><span>Matricule debut</span><input value={matriculeDebut} onChange={(e) => setMatriculeDebut(e.target.value)} /></label>
                    <label style={{ width: "120px" }}><span>Matricule fin</span><input value={matriculeFin} onChange={(e) => setMatriculeFin(e.target.value)} /></label>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    <button className="pcyc-btn" style={btnAccent} type="button" onClick={downloadJournal}><Download size={15} /> Journal PDF</button>
                </div>
            </div>

            <div className="pcyc-toolbar" style={{ borderBottom: "none", borderTopLeftRadius: "10px", borderTopRightRadius: "10px", margin: "14px 18px 0", background: "#f8fafc" }}>
                <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                    <button className="pcyc-btn" style={btnPrimary}><BookOpen size={14} /> Journal ({filteredBulletins.length})</button>
                </div>
                <div className="pcyc-search" style={{ minWidth: "220px", marginLeft: "auto" }}>
                    <Search size={14} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher collaborateur..." style={{ padding: "6px 10px 6px 30px" }} />
                </div>
            </div>

            <div className="pcyc-table-card" style={{ marginTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                <table className="pcyc-table">
                    <thead>
                    <tr><th>Matricule</th><th>Collaborateur</th><th>Salaire Base</th><th>Jours</th><th>Brut Imposable</th><th>Cotis. Sal.</th><th>IR</th><th>Cotis. PP</th><th>Net à payer</th></tr>
                    </thead>
                    <tbody>
                    {filteredBulletins.length === 0
                        ? <tr><td colSpan="9" className="pcyc-empty">Aucun bulletin calcule pour cette periode</td></tr>
                        : filteredBulletins.map((b) => {
                            const u = users.find((user) => user.id === b.userId);
                            return (
                                <tr key={b.id}>
                                    <td><span className="pcyc-code">{u?.employeeCode || b.employeeCode || "N/A"}</span></td>
                                    <td><strong>{userName(u)}</strong></td>
                                    <td>{money(b.salaireBase)}</td>
                                    <td>{money(b.nombreJoursTravailles)}</td>
                                    <td>{money(b.salaireBrutImposable)}</td>
                                    <td>{money(b.totalCotisationsSalariales)}</td>
                                    <td>{money(b.ir)}</td>
                                    <td>{money(b.totalCotisationsPatronales)}</td>
                                    <td><strong>{money(b.netAPayer)}</strong></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
